/**
 * Terms of Service / Privacy Policy acceptance gate — client side (#756).
 *
 * The backend login gate (#555) blocks un-accepted users, redirecting HTML
 * loads to `/accept-terms` and answering `/api/*` with a 403. That covers full
 * page loads and API-driven views, but a blocked user navigating client-side to
 * a view that fires no gate-checked API call would otherwise sit there without
 * being returned to the acceptance page. To close that gap the server injects a
 * `bf-needs-acceptance` meta tag (the same predicate the gate uses); the SPA
 * checks it on every route change and redirects proactively.
 */

import { navigation } from '@brainfocus/web/api/client';

// Set once the user re-accepts in this session. The server-rendered meta tag is
// stale until the next full load, so without this the post-accept navigation to
// `next` would bounce straight back to `/accept-terms`.
let acceptanceSatisfied = false;

/** Mark acceptance as satisfied for the rest of this session (loop guard). */
export function markAcceptanceSatisfied(): void {
    acceptanceSatisfied = true;
}

/** Whether the current user still needs to accept the current legal documents. */
export function needsAcceptance(): boolean {
    if (acceptanceSatisfied) {
        return false;
    }
    const meta = document.head.querySelector('meta[name=bf-needs-acceptance]') as HTMLMetaElement | null;
    return meta?.content === 'true';
}

/**
 * Redirect the browser (full-page) to `/accept-terms?next=<current path>` when
 * acceptance is still required and we are not already on that page. Returns true
 * if a redirect was issued. Full-page navigation tears down the current view and
 * lets the backend gate re-evaluate the load as HTML — consistent with the
 * `execFetch` 403 handling.
 */
export function redirectToAcceptanceIfNeeded(): boolean {
    if (!needsAcceptance()) {
        return false;
    }
    if (window.location.pathname === '/accept-terms') {
        return false;
    }
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    navigation.assign(`/accept-terms?next=${next}`);
    return true;
}

/** Test-only: reset the module-level session flag between cases. */
export function __resetAcceptanceStateForTests(): void {
    acceptanceSatisfied = false;
}
